
























































































































































import moment from 'moment';
import * as R from 'ramda';
import Vue from 'vue';
import XLSX from 'xlsx';

import { ClientProduct, Job, Outlet } from '@/core/entity';

import { getJobQL, getJobResultQL, JobResult } from './graphql';

interface Count {
  group: string;
  count: number;
}

export default Vue.extend({
  props: {
    jobId: String as () => string,
    visible: Boolean as () => boolean,
    showDelete: {
      default: true,
      type: Boolean,
    }
  },

  apollo: {
    job: {
      query: getJobQL,
      variables() {
        return { id: this.jobId };
      },
      skip() {
        return !this.jobId;
      },
      fetchPolicy: 'network-only',
    },
  },

  data() {
    return {
      busy: false,
      job: null as Job | null,
    };
  },

  computed: {
    clientName(): string {
      if (this.job) {
        return this.job.client!.name;
      }
      return '';
    },

    jobDate(): string {
      if (this.job) {
        return moment(this.job.date).format('DD MMM YYYY');
      }
      return '';
    },

    jobNote(): string {
      return this.job ? this.job.description || '' : '';
    },

    outlets(): Outlet[] {
      if (this.job) {
        return this.job.outlets!;
      }
      return [];
    },

    productCount(): Count[] {
      if (this.job && this.job.products) {
        const summary = R.pipe(
          R.reject<ClientProduct, 'array'>((c) => c.intrackProduct === null),
          R.groupBy<ClientProduct>((c) => {
            const parts = c.intrackProduct!.category!.canonicalName!.split(
              ' / ',
            );
            return parts[1];
          }),
          R.mapObjIndexed((list: ClientProduct[]) => {
            return list.length;
          }),
          R.toPairs,
          R.map((p) => ({ group: p[0], count: p[1] })),
          R.sortWith([R.ascend(R.prop('group'))]),
        )(this.job.products);

        return summary;
      }
      return [];
    },

    productTotal(): number {
      return R.reduce((acc, el) => acc + el.count, 0, this.productCount);
    },
  },

  methods: {
    async deleteJob() {
      this.busy = true;

      const { jobId } = this;

      const result = await this.$coreApp.job.remove(
        jobId,
        this.presenter('Job removed.', 'Unable to remove job:'),
        undefined,
        undefined,
        [`listJob`],
      );
    },

    async exportResult() {
      this.busy = true;

      const { jobId, job } = this;
      try {
        const result = await this.$apollo.query({
          query: getJobResultQL,
          variables: {
            id: jobId,
          },
        });

        const wb = XLSX.utils.book_new();

        const data = R.pipe(
          R.groupBy<JobResult>(R.prop('outletId')),
          R.values,
          R.forEach((res) => {
            const first = R.head(res);
            const headers = ['ID', 'Name', 'Price', 'In-store', 'OOS', 'Note'];
            const rows = R.map((r) => {
              return [
                r.productId,
                r.productName,
                r.price ? r.price!.toFixed(2) : '-',
                r.isInStorePromo,
                r.isOos,
                r.note,
              ];
            }, res);

            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            XLSX.utils.book_append_sheet(wb, ws, first!.outletName.substring(0, 30));
          }),
        )(result.data.jobResult as JobResult[]);

        XLSX.writeFile(wb, `${job!.date}-${job!.description}.xlsx`);
      } catch (err) {
        console.error(err);
        this.$message.error('Unable to export job result');
      } finally {
        this.busy = false;
      }
    },

    async retrieveImages() {
      this.busy = true;

      const { jobId } = this;
      const user = this.$store.state.user;

      const result = await this.$coreApp.job.getImages(
          jobId,
          user.email,
          this.presenter('Images are being generated.', 'Unable to generate images:'),
      );
    },

    onHideDrawer() {
      this.$emit('close');
    },

    presenter(success: string, failure: string) {
      return (err: Error | null, result: Job | boolean | null, loading: boolean) => {
        this.busy = loading;
        if (loading) {
          // request in progress
          return;
        }

        if (err) {
          this.$message.error(`${failure} ${err.message}`);
        } else {
          this.$message.success(success);

          this.$router.back();
        }
      };
    },

    showDeleteConfirm() {
      const { deleteJob } = this;
      this.$confirm({
        title: 'Are you sure you want to delete this job?',
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk() {
          deleteJob();
        },
      });
    },

    showExportConfirm() {
      const { exportResult } = this;
      this.$confirm({
        title: 'Are you sure you want to export this job?',
        okText: 'Yes',
        okType: 'primary',
        cancelText: 'No',
        onOk() {
          exportResult();
        },
      });
    },

    showExportImageConfirm() {
      const { retrieveImages } = this;
      this.$confirm({
        title: 'System will generate a zip for all the images uploaded. You will receive an email when the file is ready.',
        okText: 'Yes',
        okType: 'primary',
        cancelText: 'No',
        onOk() {
          retrieveImages();
        },
      });
    },
  },
});
